var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// (c) 2023 Acellera Ltd http://www.acellera.com
// All Rights Reserved
// No redistribution in whole or part
//
import { useState, useEffect, useRef } from "react";
import { Box, Grid } from "@mui/material";
import { LLMChatInput } from "./LLMChatInput/LLMChatInput";
import produce from "immer";
import { throttle } from "../utils/throttle";
import { dispatchConfirmationDialogEvent } from "../utils";
import { handleLoadSMILES } from "./handleLoadSMILES";
import { filterImages, loadImage } from "./utils";
import UserContent from "./LLMChatMessages/UserContent";
import AssistantContent from "./LLMChatMessages/AssistantContent";
import { LLMChatButtons } from "./LLMChatButtons";
import systemPrompt from "./systemPrompt";
import uuidv4 from "../utils/UUID";
export function LLMChat({ vss, molstar, pyodide, hideChat, textColor, disabled, userId, }) {
    const _handleLoadSMILES = (smiles) => {
        handleLoadSMILES(vss, molstar, pyodide, smiles);
    };
    const [chatId, setChatId] = useState(null);
    useEffect(() => {
        setChatId(uuidv4());
    }, []);
    const [messages, setMessages] = useState([]);
    const [completing, setCompleting] = useState(false);
    const [waitingResponse, setWaitingResponse] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const chatContainerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const systemData = {
        role: "system",
        content: [
            {
                type: "text",
                text: systemPrompt,
            },
        ],
    };
    const handleScroll = () => {
        if (chatContainerRef.current) {
            const atBottom = chatContainerRef.current.scrollHeight -
                chatContainerRef.current.scrollTop ===
                chatContainerRef.current.clientHeight;
            setIsAtBottom(atBottom);
        }
    };
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.addEventListener("scroll", handleScroll);
            return () => {
                var _a;
                (_a = chatContainerRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener("scroll", handleScroll);
            };
        }
    }, []);
    const updateMessageInProgress = throttle((message) => {
        setMessages((prev) => {
            const newMessages = produce(prev, (draft) => {
                const lastMessage = draft[draft.length - 1];
                if (!lastMessage)
                    return;
                if (lastMessage.role === "assistant") {
                    lastMessage.content = [{ type: "text", text: message }];
                }
                else {
                    draft.push({
                        role: "assistant",
                        content: [{ type: "text", text: message }],
                    });
                }
            });
            return newMessages;
        });
    }, 100);
    const setAssistantMessageError = (error) => {
        setMessages((prev) => {
            const newMessages = produce(prev, (draft) => {
                draft[draft.length - 1]["error"] = true;
                draft.push({
                    role: "assistant",
                    content: [{ type: "text", text: error }],
                    error: true,
                });
            });
            return newMessages;
        });
    };
    const streamModelResponse = (response) => __awaiter(this, void 0, void 0, function* () {
        if (!response.body)
            return "";
        let message = "";
        let decoder = new TextDecoder("utf-8");
        let isDone = false;
        const reader = response.body.getReader();
        while (true) {
            // Process each chunk:
            const { done, value } = yield reader.read();
            if (done) {
                isDone = true;
                break;
            }
            const chunk = decoder.decode(value, { stream: true });
            const chunkElements = chunk.split("\n");
            let somethingInChunk = false;
            let i = 0;
            for (const chunkData of chunkElements) {
                i++;
                if (!chunkData)
                    continue;
                const chunkCleaned = chunkData.replace(/^data:\s*/, "");
                if (chunkCleaned === "[DONE]") {
                    isDone = true;
                    break;
                }
                let delta;
                try {
                    const chunkJSON = JSON.parse(chunkCleaned);
                    delta = chunkJSON.choices[0].delta;
                }
                catch (_a) {
                    console.error("Error parsing chunk:");
                }
                if (!delta || !delta.content)
                    continue;
                message += delta.content;
                somethingInChunk = true;
            }
            if (somethingInChunk) {
                updateMessageInProgress(message);
                setWaitingResponse(false);
            }
            if (isDone) {
                break;
            }
        }
        return message;
    });
    const handleStopAnswer = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            setWaitingResponse(false);
            setCompleting(false);
        }
    };
    const handleSendMessage = (input, file, system) => __awaiter(this, void 0, void 0, function* () {
        if (disabled)
            return;
        if (!input.trim())
            return;
        if (completing)
            return;
        if (!chatId)
            return;
        setCompleting(true);
        setWaitingResponse(true);
        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();
        let fileContent;
        if (file) {
            fileContent = yield loadImage(file);
        }
        const textContent = { type: "text", text: input };
        const newContent = fileContent
            ? [
                {
                    type: "image_url",
                    image_url: {
                        url: fileContent,
                    },
                },
                textContent,
            ]
            : [textContent];
        if (system) {
            newContent.push({
                type: "text",
                text: JSON.stringify(system),
            });
        }
        const updatedMessages = [
            ...messages,
            { role: "user", content: newContent },
        ];
        setMessages(updatedMessages);
        const sentMessages = [
            systemData,
            ...updatedMessages.filter((e) => !e.error),
        ];
        const sentMessagesImgFilt = filterImages(sentMessages);
        try {
            const response = yield fetchModelQuery(sentMessagesImgFilt, chatId, userId, abortControllerRef.current.signal);
            if (!response.ok) {
                console.error("Error:", response.status, response.statusText);
                const respJson = yield response.json();
                throw new Error(respJson.message);
            }
            if (response.body) {
                // {"object":"error","message":"This model's maximum context length is 32768 tokens. However, you requested 78744 tokens in the messages, Please reduce the length of the messages.","type":"BadRequestError","param":null,"code":400}
                const message = yield streamModelResponse(response);
                if (!message)
                    throw new Error("No response received.");
            }
            else {
                console.log("NOT STREAMED");
                const data = yield response.json();
                const message = data.choices[0].message.content;
                if (!message)
                    throw new Error("No response received.");
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", content: message },
                ]);
            }
        }
        catch (error) {
            if (error.name === "AbortError") {
                console.log("Request was aborted");
            }
            else {
                console.error("Error:", error);
                setAssistantMessageError(`${error}`);
            }
        }
        finally {
            setWaitingResponse(false);
            setCompleting(false);
            abortControllerRef.current = null;
        }
    });
    useEffect(() => {
        // Set scroll to bottom of the chat is user sends a new message or if assistant's message is received while being at the bottom of the chat.
        if (!chatContainerRef.current)
            return;
        const lastMessage = messages[messages.length - 1];
        if (isAtBottom || (lastMessage && lastMessage.role === "user")) {
            chatContainerRef.current.scrollTop =
                chatContainerRef.current.scrollHeight;
        }
    }, [messages]);
    const handleClickHide = () => {
        hideChat();
    };
    const resetChat = () => {
        handleStopAnswer();
        setMessages([]);
        setChatId(uuidv4());
    };
    const handleClickReset = () => __awaiter(this, void 0, void 0, function* () {
        const ok = yield dispatchConfirmationDialogEvent({
            message: "Are you sure you want to reset the chat?",
        });
        if (ok) {
            resetChat();
        }
    });
    useEffect(() => {
        if (disabled) {
            resetChat();
        }
    }, [disabled]);
    const handleClickDownload = () => {
        if (disabled)
            return;
        const content = JSON.stringify(messages, null, 2);
        const blob = new Blob([content], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "chat.json";
        a.click();
        URL.revokeObjectURL(url);
    };
    return (_jsxs(Grid, Object.assign({ container: true, direction: "column", sx: { height: "100%" }, wrap: "nowrap" }, { children: [_jsx(LLMChatButtons, { handleClickHide: handleClickHide, handleClickReset: handleClickReset, handleClickDownload: handleClickDownload, textColor: textColor, disableReset: waitingResponse || messages.length === 0 }), _jsx(Grid, Object.assign({ item: true, xs: true, sx: { overflowY: "auto" } }, { children: _jsxs(Box, Object.assign({ display: "flex", flexDirection: "column", height: "100%" }, { children: [_jsx(Box, Object.assign({ ref: chatContainerRef, flexGrow: 1, display: "flex", flexDirection: "column", 
                            // p={2}
                            overflow: "auto", mb: 2, p: 2 }, { children: messages.map((message, index) => {
                                return message.role === "user" ? (_jsx(UserContent, { content: message.content }, index)) : (_jsx(AssistantContent, { message: message, handleLoadSMILES: _handleLoadSMILES, waiting: waitingResponse, completing: completing, isLastMessage: index + 1 === messages.length }, index));
                            }) })), _jsx(Box, Object.assign({ display: "flex", alignItems: "center", p: 2 }, { children: _jsx(LLMChatInput, { waitingResponse: waitingResponse || !!disabled, completing: completing || !!disabled, sendMessage: handleSendMessage, stopAnswer: handleStopAnswer, vss: vss }) }))] })) }))] })));
}
const fetchModelQuery = (sentMessages, chatId, userId, signal) => __awaiter(void 0, void 0, void 0, function* () {
    console.log("******");
    const baseUrl = process.env.NODE_ENV === "development"
        ? "http://10.183.20.119:8001"
        : window.location.origin;
    return yield fetch(`${baseUrl}/v1/chat/completions`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer no-key",
        },
        body: JSON.stringify({
            model: "gemma-lora",
            messages: sentMessages,
            stream: true,
            chatId: chatId,
            userId: userId,
        }),
        signal,
    });
});
